import { BackgroundDots } from "@msuite/ui/background";
import { Button } from "@msuite/ui/button";
import { ImageUpload, useFileUpload } from "@msuite/ui/file-upload";
import { AuthForm, OTPField, PasswordIndicator, TextInput } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { type UserSignUpInviteSchema, userSignUpInviteSchema } from "@msuite/utils/schemas";
import type { Tables } from "@msuite/utils/types";
import { useForm, useWatch } from "react-hook-form";
import { useUpload } from "@/api/files";

/** Props Interface */
interface SignUpInviteCreateProps {
	/** What the invitation route ships to an anonymous visitor: the address to prefill, and no more. */
	invitation: Pick<Tables<"userInvitations">, "id" | "email" | "state">;
}

export function SignUpInviteCreate({ invitation }: SignUpInviteCreateProps) {
	/** Hooks */
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
	});
	const { t } = useTranslation();

	/** Form */
	const methods = useForm({
		defaultValues: {
			email: invitation.email,
		},
		mode: "onSubmit",
		resolver: resolver(userSignUpInviteSchema),
	});

	const [password] = useWatch({
		control: methods.control,
		name: ["password"],
	});

	/** Functions */
	async function handleOnSubmit(_data: UserSignUpInviteSchema) {
		try {
			// TODO: Implement this function
			console.info("handleOnSubmit", _data);
		} catch (error) {
			console.error(error);
			toast.error((error as Error).message);
		}
	}

	/** Render */
	return (
		<div className="flex-center w-screen h-screen">
			<BackgroundDots />
			<AuthForm onSubmit={methods.handleSubmit(handleOnSubmit)}>
				<AuthForm.Header />
				<AuthForm.Body>
					<ImageUpload
						controller={controller}
						type="image"
						renderFile={() => null}
					/>
					<AuthForm.Row>
						<TextInput
							label={t("profile.first_name")}
							placeholder="Max"
							control={methods.control}
							name="firstName"
							isRequired
						/>
						<TextInput
							label={t("profile.last_name")}
							placeholder="Mustermann"
							control={methods.control}
							name="lastName"
							isRequired
						/>
					</AuthForm.Row>
					<TextInput
						label={t("personnel.fields.email")}
						placeholder="max@mustermann.de"
						control={methods.control}
						name="email"
						isDisabled
						isRequired
					/>
					<OTPField
						control={methods.control}
						name="otp"
						label={"OTP"}
						isRequired
					/>
					<div className="v-stack">
						<TextInput
							type="password"
							label={t("auth.password")}
							isRequired
							control={methods.control}
							name="password"
							placeholder="Password"
						/>
						<PasswordIndicator currentPassword={password} />
					</div>
					<TextInput
						type="password"
						label={t("auth.confirm_password")}
						isRequired
						control={methods.control}
						name="confirmPassword"
						placeholder="Password"
					/>
				</AuthForm.Body>
				<AuthForm.Footer>
					<Button
						type="submit"
						variant="primary"
						size="sm"
						isLoading={methods.formState.isSubmitting}
					>
						{t("general.submit")}
					</Button>
				</AuthForm.Footer>
			</AuthForm>
		</div>
	);
}
